import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Wallet, Sparkles, ShieldCheck } from "lucide-react";

const FEATURES = [
  "List as many deals as you want",
  "Keep 100% of every booking",
  "Phone, email, or your own booking link",
  "Cancel any time",
];

export default function ForTherapists() {
  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1600334129128-685c5582fd35?auto=format&fit=crop&w=1920&q=80" alt="Black male massage therapist giving a hot-stone massage" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F9F7F3] via-[#F9F7F3]/85 to-[#F9F7F3]/20"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">For therapists & studios</div>
          <h1 className="mt-3 font-serif-display text-5xl sm:text-6xl tracking-tighter leading-[1.02] max-w-3xl">A fair marketplace<br/>for your craft.</h1>
          <p className="mt-6 text-lg text-[#2C352D]/80 max-w-2xl leading-relaxed">Flat monthly pricing. Zero commission on bookings. You set the deal, you set the redemption — we just bring the customers.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6 pb-20">
        <PriceCard
          tier="Individual therapist"
          price="$49.99"
          tagline="Independent, licensed therapist"
          features={FEATURES}
        />
        <PriceCard
          tier="Massage business"
          price="$99.99"
          tagline="Spa, studio, or clinic"
          features={[...FEATURES, "Multiple practitioners on one account"]}
          featured
        />
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { icon: Wallet, t: "Flat. Predictable.", b: "$49.99 or $99.99 per deal per month. That's the whole pricing page." },
          { icon: ShieldCheck, t: "You stay in control", b: "Choose how customers reach you: phone, your booking URL, or email." },
          { icon: Sparkles, t: "Beautiful by default", b: "We give your listing the love it deserves — calm photos, clear pricing." },
        ].map((it, i) => (
          <div key={i} className="bg-white p-8 rounded-2xl border border-[#E8E2D6]">
            <it.icon className="w-6 h-6 text-[#D97757]" strokeWidth={1.5} />
            <h3 className="mt-5 font-serif-display text-2xl">{it.t}</h3>
            <p className="mt-2 text-[#5F6C60] leading-relaxed">{it.b}</p>
          </div>
        ))}
      </section>
    </div>
  );
}

function PriceCard({ tier, price, tagline, features, featured }) {
  return (
    <div className={`rounded-3xl p-10 border ${featured ? 'bg-[#0F766E] text-[#F9F7F3] border-[#0F766E]' : 'bg-white border-[#E8E2D6] text-[#2C352D]'}`}>
      <div className={`text-xs font-bold uppercase tracking-[0.2em] ${featured ? 'text-[#F9F7F3]/80' : 'text-[#5F6C60]'}`}>{tier}</div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-serif-display text-6xl">{price}</span>
        <span className={`${featured ? 'text-[#F9F7F3]/70' : 'text-[#5F6C60]'} text-sm`}>per deal / month</span>
      </div>
      <p className={`${featured ? 'text-[#F9F7F3]/80' : 'text-[#5F6C60]'} mt-2`}>{tagline}</p>
      <ul className="mt-8 space-y-3">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-3">
            <Check className={`w-5 h-5 mt-0.5 ${featured ? 'text-[#D97757]' : 'text-[#0F766E]'}`} strokeWidth={1.5} />
            <span className="text-sm">{f}</span>
          </li>
        ))}
      </ul>
      <Link to="/register">
        <Button data-testid={`pricing-cta-${featured ? 'business' : 'individual'}`} className={`mt-10 w-full rounded-full h-12 ${featured ? 'bg-[#D97757] hover:bg-[#C26243] text-white' : 'bg-[#0F766E] hover:bg-[#0C5F58] text-white'}`}>
          Get started
        </Button>
      </Link>
    </div>
  );
}
